import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, TextField, Grid } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Cropper from 'react-easy-crop';
import { readFile, getCroppedImg } from '../../../utils/ImageUtils';
import { updateStaffProfile, uploadProfilePicture } from '../../../services/UserService';
import LoadingButton from '@mui/lab/LoadingButton';
import dayjs from 'dayjs';

const EditPersonalInfoModal = ({ open, onClose, user, onSave }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [firstName, setFirstName] = useState(user.firstName);
    const [lastName, setLastName] = useState(user.lastName);
    const [phone, setPhone] = useState(user.phone);
    const [address, setAddress] = useState(user.address);
    const [birthDate, setBirthDate] = useState(user.birthDate ? dayjs(user.birthDate) : null);
    const [errors, setErrors] = useState({ firstName: '', lastName: '', phone: '', address: '', birthDate: '' });
    const [imageSrc, setImageSrc] = useState(user.profilePictureUrl || '');
    const [croppedFile, setCroppedFile] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);

    useEffect(() => {
        setFirstName(user.firstName);
        setLastName(user.lastName);
        setPhone(user.phone);
        setAddress(user.address);
        setBirthDate(user.birthDate ? dayjs(user.birthDate) : null);
        setImageSrc(user.profilePictureUrl || '');
        setErrors({ firstName: '', lastName: '', phone: '', address: '', birthDate: '' });
    }, [user, open]);

    const validate = () => {
        let tempErrors = { firstName: '', lastName: '', phone: '', address: '', birthDate: '' };
        let isValid = true;

        if (!firstName.trim()) {
            tempErrors.firstName = t('first_name_required');
            isValid = false;
        }

        if (!lastName.trim()) {
            tempErrors.lastName = t('last_name_required');
            isValid = false;
        }

        if (!address.trim()) {
            tempErrors.address = t('address_required');
            isValid = false;
        }

        if (!phone.trim()) {
            tempErrors.phone = t('phone_required');
            isValid = false;
        } else if (!/^\d+$/.test(phone)) {
            tempErrors.phone = t('phone_required_only_numbers');
            isValid = false;
        }

        if (!birthDate) {
            tempErrors.birthDate = t('birth_date_required');
            isValid = false;
        }

        setErrors(tempErrors);
        return isValid;
    };

    const handleSave = async () => {
        if (validate()) {
            try {
                setLoading(true);
                if (croppedFile) {
                    await uploadProfilePicture(croppedFile);
                }
                const formattedBirthDate = birthDate.format('YYYY-MM-DDT00:00:00');
                const request = { firstName, lastName, phone, address, birthDate: formattedBirthDate };
                const updatedUser = await updateStaffProfile(request);
                onSave(updatedUser);
                setLoading(false);
                onClose();
            } catch (error) {
                setLoading(false);
                console.error('Error updating profile:', error);
                // Handle the error appropriately here
            }
        }
    };

    const onCropComplete = useCallback(async (croppedArea, croppedAreaPixels) => {
        try {
            const { file } = await getCroppedImg(imageSrc, croppedAreaPixels);
            setCroppedFile(file);
        } catch (e) {
            console.error(e);
        }
    }, [imageSrc]);

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const imageDataUrl = await readFile(file);
            setImageSrc(imageDataUrl);
            setCroppedFile(file);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle><span className="roboto-light">{t('edit_personal_information')}</span></DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoFocus
                            margin="normal"
                            label={t('first_name')}
                            type="text"
                            fullWidth
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            error={!!errors.firstName}
                            helperText={errors.firstName}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            margin="normal"
                            label={t('last_name')}
                            type="text"
                            fullWidth
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            error={!!errors.lastName}
                            helperText={errors.lastName}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            margin="normal"
                            label={t('phone')}
                            type="text"
                            fullWidth
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            error={!!errors.phone}
                            helperText={errors.phone}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} style={{paddingTop: '32px'}}>
                        <DatePicker
                            label={t('birth_date')}
                            value={birthDate}
                            margin="normal"
                            format="DD/MM/YYYY"
                            onChange={(newValue) => setBirthDate(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    margin="normal"
                                    error={!!errors.birthDate}
                                    helperText={errors.birthDate}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
                <TextField
                    margin="normal"
                    label={t('address')}
                    type="text"
                    fullWidth
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    error={!!errors.address}
                    helperText={errors.address}
                />
                <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="upload-photo"
                    type="file"
                    onChange={handleImageChange}
                />
                <label htmlFor="upload-photo">
                    <Button variant="text" color="primary" component="span" style={{ marginTop: '15px' }}>
                        {t('upload_photo')}
                    </Button>
                </label>
                {imageSrc && (
                    <div style={{ position: 'relative', width: '100%', height: 200 }}>
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                        />
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('cancel')}</Button>
                <LoadingButton loading={loading} onClick={handleSave} variant="contained">{t('update')}</LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default EditPersonalInfoModal;
